import '@fontsource-variable/inter';
import { Fragment, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { SessionProvider, useSession } from './session';
import { installGermanValidation } from './nativeValidation';
import { AppShell } from './components/AppShell';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { InquiriesPage } from './pages/InquiriesPage';
import { InquiryDetailPage } from './pages/InquiryDetailPage';
import { NewInquiryPage } from './pages/NewInquiryPage';
import { SetupPage } from './pages/SetupPage';
import { SetupIndex, SetupLayout } from './pages/SetupLayout';
import { SetupPlaceholderPage } from './pages/SetupPlaceholderPage';
import { RolesPage } from './pages/RolesPage';
import { WirkiaAccountPage } from './pages/WirkiaAccountPage';
import { StoragePage } from './pages/StoragePage';
import { DataDeletionPage } from './pages/DataDeletionPage';
import { NumberPrefixesPage } from './pages/NumberPrefixesPage';
import { OrderPage } from './pages/OrderPage';
import { ReactivationPage } from './pages/ReactivationPage';
import { RideInvoicePage } from './pages/RideInvoicePage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { PaymentResultPage } from './pages/PaymentResultPage';
import { OperatorBillingPage } from './operator/OperatorBillingPage';
import { UsersPage } from './pages/UsersPage';
import { UserFormPage } from './pages/UserFormPage';
import { AcceptInvitationPage, ResetPasswordPage } from './pages/SetPasswordPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { AccountDetailsPage, AccountIndex, AccountLayout, AccountAccessPage, AccountRightsPage } from './pages/AccountPage';
import { MyEmployeeProfile, MyFiles, MyLeaveRequests, MyVacation } from './modules/hr';
import { MyShifts } from './modules/shifts';
import { SETUP_SECTIONS } from './setupSections';
import { InquiryTypePage } from './pages/InquiryTypePage';
import { ModuleGate } from './components/ModuleGate';
import { OperatorLayout } from './operator/OperatorLayout';
import { OperatorLoginPage } from './operator/OperatorLoginPage';
import { OperatorMailTemplatePage } from './operator/OperatorMailTemplatePage';
import { OperatorPlansPage } from './operator/OperatorPlansPage';
import { OperatorIndustriesPage } from './operator/OperatorIndustriesPage';
import { OperatorTenantPage } from './operator/OperatorTenantPage';
import { OperatorTenantsPage } from './operator/OperatorTenantsPage';
import { backofficeModules } from './modules/registry';
import { PublicOfferPage } from './modules/offers';
import { PublicContractPage, PublicTerminationPage } from './modules/contracts';
import { NewsletterConfirmPage, NewsletterUnsubscribePage } from './modules/marketing/PublicNewsletterPage';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

function Protected({ children }: { children: React.ReactNode }) {
  const { session, loading, error } = useSession();
  if (loading) return <div className="center-screen">Wird geladen …</div>;
  if (error) {
    return (
      <div className="center-screen">
        <div className="card narrow">
          <h1>Zugang nicht möglich</h1>
          <p className="muted">{error.message}</p>
        </div>
      </div>
    );
  }
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

installGermanValidation();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/betreiber/anmelden" element={<OperatorLoginPage />} />
            <Route path="/betreiber" element={<OperatorLayout />}>
              <Route index element={<OperatorTenantsPage />} />
              <Route path="mandanten/:id" element={<OperatorTenantPage />} />
              <Route path="zahlungen" element={<OperatorBillingPage />} />
              <Route path="mailvorlagen" element={<OperatorMailTemplatePage />} />
              <Route path="pakete" element={<OperatorPlansPage />} />
              <Route path="branchen" element={<OperatorIndustriesPage />} />
            </Route>
            <Route path="/einladung/:token" element={<AcceptInvitationPage />} />
            <Route path="/zahlung/:result" element={<PaymentResultPage />} />
            <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
            <Route path="/passwort-zuruecksetzen/:token" element={<ResetPasswordPage />} />
            <Route path="/angebot/:token" element={<PublicOfferPage />} />
            <Route path="/weiterbestellen/:token" element={<ReactivationPage />} />
            <Route path="/vertrag/:token" element={<PublicContractPage />} />
            <Route path="/kuendigung/:token" element={<PublicTerminationPage />} />
            <Route path="/newsletter/bestaetigen/:token" element={<NewsletterConfirmPage />} />
            <Route path="/newsletter/abmelden/:token" element={<NewsletterUnsubscribePage />} />
            <Route
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="konto" element={<AccountLayout />}>
                <Route index element={<AccountIndex />} />
                <Route path="angaben" element={<AccountDetailsPage />} />
                <Route path="rechte" element={<AccountRightsPage />} />
                <Route path="zugang" element={<AccountAccessPage />} />
                <Route path="passwort" element={<Navigate to="/konto/zugang" replace />} />
                <Route path="mitarbeiter" element={<MyEmployeeProfile />} />
                <Route path="urlaub" element={<MyVacation />} />
                <Route path="antraege" element={<MyLeaveRequests />} />
                <Route path="schichten" element={<MyShifts />} />
                <Route path="dateien" element={<MyFiles />} />
              </Route>
              <Route path="anfragen" element={<ModuleGate module="inquiries" permission="inquiries.read"><InquiriesPage /></ModuleGate>} />
              <Route path="anfragen/neu" element={<ModuleGate module="inquiries" permission="inquiries.write"><NewInquiryPage /></ModuleGate>} />
              <Route path="anfragen/:id" element={<ModuleGate module="inquiries" permission="inquiries.read"><InquiryDetailPage /></ModuleGate>} />
              {backofficeModules.map((m) => <Fragment key={m.key}>{m.routes}</Fragment>)}
              <Route path="bestellen" element={<ModuleGate module="setup" permission="setup.billing"><OrderPage /></ModuleGate>} />
              <Route path="einrichtung" element={<SetupLayout />}>
                <Route index element={<SetupIndex />} />
                <Route path="anfragetypen" element={<ModuleGate module="setup" permission="setup.read"><SetupPage /></ModuleGate>} />
                <Route path="anfragetypen/neu" element={<ModuleGate module="setup" permission="setup.write"><InquiryTypePage /></ModuleGate>} />
                <Route path="anfragetypen/:key" element={<ModuleGate module="setup" permission="setup.write"><InquiryTypePage /></ModuleGate>} />
                <Route path="wirkia-konto" element={<ModuleGate module="setup" permission="setup.read"><WirkiaAccountPage /></ModuleGate>} />
                <Route path="branche" element={<Navigate to="/einrichtung/firmendaten" replace />} />
                <Route path="ueber" element={<Navigate to="/einrichtung/wirkia-konto" replace />} />
                <Route path="abrechnung" element={<Navigate to="/einrichtung/wirkia-konto" replace />} />
                <Route path="speicher" element={<ModuleGate module="setup" permission="setup.storage"><StoragePage /></ModuleGate>} />
                <Route path="webseite" element={<Navigate to="/einrichtung/api/anfragen" replace />} />
                <Route path="chat" element={<Navigate to="/einrichtung/api/chat" replace />} />
                <Route path="fahrtrechnung" element={<ModuleGate module="invoices_out" permission="setup.read"><RideInvoicePage /></ModuleGate>} />
                <Route path="nummernkennungen" element={<ModuleGate module="setup" permission="setup.read"><NumberPrefixesPage /></ModuleGate>} />
                <Route path="daten-loeschen" element={<ModuleGate module="setup" permission="setup.wipe"><DataDeletionPage /></ModuleGate>} />
                <Route path="api" element={<ModuleGate module="setup" permission="setup.integrations"><IntegrationsPage /></ModuleGate>} />
                <Route path="api/:key" element={<ModuleGate module="setup" permission="setup.integrations"><IntegrationsPage /></ModuleGate>} />
                <Route path="benutzer" element={<ModuleGate module="users" permission="users.read"><UsersPage /></ModuleGate>} />
                <Route path="rollen" element={<ModuleGate module="users" permission="users.read"><RolesPage /></ModuleGate>} />
                <Route path="benutzer/neu" element={<ModuleGate module="users" permission="users.write"><UserFormPage /></ModuleGate>} />
                <Route path="benutzer/:id" element={<ModuleGate module="users" permission="users.write"><UserFormPage /></ModuleGate>} />
                {backofficeModules.map((m) => <Fragment key={m.key}>{m.setupRoutes}</Fragment>)}
                {SETUP_SECTIONS.filter((s) => !s.built).map((s) => (
                  <Route key={s.path} path={s.path} element={<ModuleGate module={s.module} permission={s.permission}><SetupPlaceholderPage section={s} /></ModuleGate>} />
                ))}
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </SessionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
